import { useState } from "react";

import { isEqual, omit } from "lodash";
import { useToasts } from "react-toast-notifications";

import { PrimaryButton } from "@shared/buttons";

import { useHasSubmittedMetric, useSelectedIsDirtyMetric } from "./atoms";
import { ConfirmDeleteButton } from "./custom-metric-inputs/confirm-delete-button";
import { useNameError } from "./formula-builder/formula-builder";
import { useCurrentlyUpdatingMetric } from "./hooks/use-currently-updating-metric";
import { useCustomMetrics } from "./hooks/use-custom-metrics";
import { useDisabledControls } from "./hooks/use-disabled-controls";
import { useFormulaBuilder } from "./hooks/use-formula-builder";
import { useFormulaHistory } from "./hooks/use-formula-history";

export const Actions = () => {
  const isDisabled = useDisabledControls();
  const {
    saveMetric,
    deleteMetric,
    loadingState,
    selectedMetric,
    customMetrics,
  } = useCustomMetrics();

  const [, setIsDirty] = useSelectedIsDirtyMetric();
  const [, setHasSubmitted] = useHasSubmittedMetric();
  const { currentlyUpdatingMetric } = useCurrentlyUpdatingMetric();
  const { formula, formulaEvaluator, isFormulaValid } = useFormulaBuilder();
  const { clearHistory } = useFormulaHistory();
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const { addToast } = useToasts();

  const hasChanged =
    !isEqual(omit(selectedMetric, ["__typename"]), currentlyUpdatingMetric) ||
    (isFormulaValid && !formulaEvaluator.compareAst(selectedMetric?.formula));

  const nameErrorMessage = useNameError(
    currentlyUpdatingMetric?.name ?? "",
    customMetrics,
    selectedMetric,
  );

  const canSave =
    !isDisabled &&
    hasChanged &&
    isFormulaValid &&
    formula.length > 0 &&
    currentlyUpdatingMetric &&
    currentlyUpdatingMetric?.name.trim() !== "" &&
    !nameErrorMessage;

  return (
    <div className="[grid-area:actions] flex items-center gap-2 justify-end pb-0.5 pr-0.5">
      <ConfirmDeleteButton
        title={`Are you sure you want to delete ${currentlyUpdatingMetric?.name}?`}
        confirmDeleteModal={confirmDeleteModal}
        onDeleteClick={() => setConfirmDeleteModal(true)}
        isLoading={loadingState.isDeleteMetricLoading}
        isDisabled={isDisabled}
        onConfirmDelete={async () => {
          setConfirmDeleteModal(false);
          await deleteMetric();
          clearHistory();
          addToast("Custom metric deleted", { appearance: "success" });
          setIsDirty(false);
        }}
        onCancelDelete={() => setConfirmDeleteModal(false)}
      />
      <PrimaryButton
        loading={
          loadingState.isUpdateMetricLoading ||
          loadingState.isCreateMetricLoading
        }
        disabled={!canSave}
        onClick={async () => {
          setHasSubmitted(true);
          if (currentlyUpdatingMetric?.description.trim() === "") {
            return;
          }

          await saveMetric(formulaEvaluator.toAst());
          clearHistory();
          addToast("Custom metric saved", { appearance: "success" });
          setIsDirty(false);
        }}
      >
        SAVE
      </PrimaryButton>
    </div>
  );
};
